<template>
    <Accordion style="margin-bottom: 0.5em;direction: rtl;" :activeIndex="filterOpened" class="w-full">
        <AccordionTab>
            <template #header>
                <div class="text-blue-500">
                    <i :class="['pi', 'pi-filter']" style="margin: 0 0.5em 0 0.5em; font-weight: bold;" />
                    <span class="text-blue-500 font-bold"> {{ $t('SEARCH') }}</span>
                </div>
            </template>
            <div id="filter_container" class="flex flex-wrap justify-content-center">
                <MultiSelect v-if="props.canFilterByOwners" :disabled="ownerFilterDisabled" v-model="selectedOwners"
                    :options="owners" optionLabel="fullName" filter :placeholder="$t('OWNER')" display="chip" style="min-width: 4rem" :pt="{ filterInput: {style: 'text-align: center'},}">
                    <template #option="slotProps">
                        <div class="flex align-items-center">
                            <img class="filter_avatar" :src="slotProps.option.imageUrl" />
                            <div>{{ slotProps.option.fullName }}</div>
                        </div>
                    </template>
                </MultiSelect>
                <InputText type="text" :placeholder="$t('BOOK_TITLE')" v-model="titleFilter" />
                <InputText type="text" :placeholder="$t('BOOK_AUTHOR')" v-model="authorFilter" />
                <InputNumber :placeholder="$t('PUBLISH_YEAR')" v-model="yearFilter" :useGrouping="false" />
                <MultiSelect :placeholder="$t('BOOK_CATEGORY')" v-model="selectedCategories" :options="categories"
                    optionLabel="name" optionValue="id" :filter="true" :pt="{ filterInput: {style: 'text-align: center'},}"/>
                
                <div class="flex gap-1">
                    <Button class="flex-1 align-items-center" @click="filterBooks">
                        <i :class="['pi', 'pi-search']" />
                        <span class="m-auto">{{ $t('SEARCH') }}</span>
                    </Button>
                    <Button class="flex-1" @click="cleanFilter">
                        <i :class="['pi', 'pi-eraser']" />
                        <span class="m-auto">{{ $t('CLEAR') }}</span>
                    </Button>
                </div>
            </div>
        </AccordionTab>
    </Accordion>
    <MyPaginator ref="paginator" :rowsPerPage="pageSize" :totalRecords="totalBooksCount" @pageSelected="changePage" />
    <div class="flex flex-wrap justify-content-center gap-2" style="margin-top: 0.5em;">
        <div v-for="book in books" :key="book" class="flex">
            <Book :data="book" :selectable="selectableBooks" @bookSelected="emitBookSelected"
                @bookUnselected="$value => $emit('book-unselected', $value)" />
        </div>
    </div>


</template>

<script setup>
import { onMounted, ref, watch } from 'vue'
import { useAuthStore } from '@/stores/auth'
import { useI18n } from 'vue-i18n'
import MyPaginator from './MyPaginator.vue'
import Book from '@/components/Book'
import BookFilter from '@/model/BookFilter'
import BooksPageRequest from '@/model/BooksPageRequest'
import bookService from '@/service/BookService'
import remove from 'lodash/remove'

import Accordion from 'primevue/accordion';
import AccordionTab from 'primevue/accordiontab';

const props = defineProps({
    pageSize: {
        type: Number,
        required: true,
    },
    totalBooksCount: {
        type: Number,
        required: true
    },
    books: {
        type: Array,
        required: true,
        default: () => []
    },
    selectableBooks: {
        type: Boolean,
        required: false,
        default: () => false
    },
    canFilterByOwners: {
        type: Boolean,
        required: false,
        default: () => false
    }
})

// Pagination data
const paginator = ref(null)
const books = ref([])
// Filters
let titleFilter = ref('')
let authorFilter = ref('')
let yearFilter = ref()
const selectedCategories = ref([])
const selectedOwners = ref([])
const filterOpened = ref(-1)
// Dropdown
const categories = ref([])
const owners = ref([])

const ownerFilterDisabled = ref(false)

const emit = defineEmits(['book-selected', 'book-unselected', 'filter-triggered', 'page-requested'])

useI18n()
const authStore = useAuthStore()

onMounted(async () => {
    const response = await bookService.getCategories()
    categories.value = response.result.collection
    owners.value = await bookService.getOwners()
    remove(owners.value, o => o.id === authStore.user.id)
})

const filterBooks = () => {
    emit('filter-triggered', makeBookFilter())
    paginator.value.resetPagination()
}

const cleanFilter = () => {
    titleFilter.value = ''
    authorFilter.value = ''
    yearFilter.value = null
    selectedCategories.value = []
    if (ownerFilterDisabled.value === false) {
        selectedOwners.value = []
    }
}

// watching single source
watch(() => props.books, (newValue) => {
    books.value = [...newValue]
})

// Events
const changePage = (requestPage) => {
    emit('page-requested', new BooksPageRequest(requestPage, makeBookFilter()))
}

function emitBookSelected(value) {
    emit('book-selected', value, makeBookFilter())
}

function makeBookFilter() {
    return new BookFilter(titleFilter.value, authorFilter.value, yearFilter.value, selectedCategories.value, selectedOwners.value.map(o => o.id))
}

function fixOwner(ownerId) {
    selectedOwners.value = [owners.value.filter(o => o.id === ownerId)[0]]
    ownerFilterDisabled.value = true
    // open the filter
    filterOpened.value = 0
}

function isOwnerFixed() {
    return ownerFilterDisabled.value === true
}

function freeOwner() {
    selectedOwners.value = []
    ownerFilterDisabled.value = false
}

function resetFilter() {
    titleFilter.value = ''
    authorFilter.value = ''
    yearFilter.value = undefined
    selectedCategories.value = []
    selectedOwners.value = []
    ownerFilterDisabled.value = false
}

function resetPagination() {
    paginator.value.resetPagination()
}

function getCurrentFilter() {
    return makeBookFilter()
}

defineExpose({
    fixOwner,
    isOwnerFixed,
    freeOwner,
    resetFilter,
    resetPagination,
    getCurrentFilter
});

</script>

<style scoped>
#filter_container>* {
    margin: 0.1em;
    width: 15em;
    height: 2.5em;
}

:deep(.p-accordion-toggle-icon) {
    display: none;
}

img.filter_avatar {
    margin-left: 0.8em;
    width: 2em;
    height: 2em;
    margin-right: 0.5rem;
    border-radius: 50%;
}


</style>